/**
 * Safe DOM construction. Everything here sets `textContent`, never `innerHTML`
 * — IFC property values and PDF metadata are attacker-controlled text
 * (CODE_RULES.md §7).
 */

type Attributes = Readonly<Record<string, string>>;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function withAttrs<T extends HTMLElement>(node: T, attrs: Attributes): T {
  for (const [key, value] of Object.entries(attrs)) {
    node.setAttribute(key, value);
  }
  return node;
}

export function mount(parent: HTMLElement, ...children: readonly HTMLElement[]): void {
  for (const child of children) parent.appendChild(child);
}

export function clear(node: HTMLElement): void {
  node.replaceChildren();
}

export function requireElement(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`Missing required element #${id}`);
  return node;
}

/** Sets `hidden` and keeps aria in step, so the two never drift apart. */
export function setVisible(node: HTMLElement, visible: boolean): void {
  node.hidden = !visible;
  node.setAttribute("aria-hidden", visible ? "false" : "true");
}
